/**
 * GTM / GA4 dataLayer helpers.
 *
 * Custom event names (per business request):
 *   - view_item_ff       → fired when the quiz finishes and the offer/result is shown
 *   - begin_checkout_ff  → fired when the visitor initiates payment on /checkout
 *   - purchase_ff        → fired on /checkout/success once Stripe confirms payment
 *
 * All payloads follow the GA4 recommended-event schema (currency, value, items[…])
 * with a `customer` object on `purchase_ff` carrying every field we can capture.
 */
import { parsePhoneNumber } from "react-phone-number-input";

// Ensure dataLayer exists even before GTM boot (idempotent no-op after).
function ensureDataLayer() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

export function pushDataLayer(event, payload = {}) {
  const dl = ensureDataLayer();
  if (!dl) return;
  // Clear ecommerce object between events per GA4 best practice.
  dl.push({ ecommerce: null });
  dl.push({ event, ...payload });
}

// ─────────────────────────────────────────────────────────────
// Item builders
// ─────────────────────────────────────────────────────────────

/** Build a GA4 item from a subscription plan variant. */
export function buildPlanItem({ funnel = "flex", plan_variant = "12mo", price, currency }) {
  const name =
    plan_variant === "6mo"
      ? "برنامج المرونة — اشتراك 6 أشهر"
      : plan_variant === "12mo"
      ? "برنامج المرونة — اشتراك 12 شهر"
      : "برنامج المرونة";
  return {
    item_id: `${funnel}_${plan_variant}`,
    item_name: name,
    item_category: funnel,
    item_variant: plan_variant,
    price: typeof price === "number" ? price : undefined,
    currency,
    quantity: 1,
  };
}

// ─────────────────────────────────────────────────────────────
// Customer builder (used on purchase_ff)
// ─────────────────────────────────────────────────────────────

/**
 * Pull a country-code (ISO-2) from an E.164 phone if possible.
 * Uses libphonenumber-js which is already bundled via react-phone-number-input.
 */
function countryFromPhone(phone) {
  if (!phone) return undefined;
  try {
    const parsed = parsePhoneNumber(phone);
    return parsed?.country;
  } catch {
    return undefined;
  }
}

export function buildCustomer({ full_name, email, whatsapp, address, city, zip, state, country }) {
  const [first_name, ...rest] = String(full_name || "").trim().split(/\s+/);
  const last_name = rest.join(" ") || undefined;
  const c = {
    name: full_name || undefined,
    first_name: first_name || undefined,
    last_name,
    email: email || undefined,
    phone: whatsapp || undefined,
    country: country || countryFromPhone(whatsapp),
    address: address || undefined,
    city: city || undefined,
    zip: zip || undefined,
    state: state || undefined,
  };
  // Strip undefined so the object stays clean in GTM's Preview mode.
  Object.keys(c).forEach((k) => c[k] === undefined && delete c[k]);
  return c;
}

// ─────────────────────────────────────────────────────────────
// Pending-purchase snapshot (persists across Stripe 3DS redirect)
// ─────────────────────────────────────────────────────────────
const PENDING_KEY = "ff_pending_purchase";

export function savePendingPurchase(snapshot) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore quota / privacy-mode failures — GA event just won't fire */
  }
}

export function readPendingPurchase() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingPurchase() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
}

/** Extract `pi_xxx` from a Stripe clientSecret `pi_xxx_secret_yyy`. */
export function paymentIntentIdFromSecret(clientSecret) {
  if (!clientSecret) return undefined;
  const [pi] = String(clientSecret).split("_secret_");
  return pi || undefined;
}
